import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import useRoutes from "./useRoutes";

const NOT_FOUND_TITLE = "Page not found";

// Route keys don't always match their url segment (dualCharts lives at
// /d3-charts), so everything here resolves by url rather than by key.
const NESTED_KEYS = ["create", "edit", "detail", "assets"];

/** A resolved route entry, or one of its nested `create`/`edit`/`detail` leaves. */
type RouteEntry = {
  url: string;
  name?: string;
  create?: RouteEntry;
  /** Set on an action that opens on the parent page rather than navigating to its own. */
  query?: Record<string, any>;
};

type Crumb = { label: string; to?: string };

const isUnder = (pathname: string, url: string) =>
  pathname === url || pathname.startsWith(`${url}/`);

const longestMatch = (entries: RouteEntry[], pathname: string) =>
  entries
    .filter(entry => isUnder(pathname, entry.url))
    .sort((a, b) => b.url.length - a.url.length)[0];

// Derives the header trail from the URL against the route table, so pages don't
// each have to declare their own crumbs. Trailing url segments are record ids
// and never become crumbs.
const usePageBreadcrumbs = () => {
  const { pathname } = useLocation();
  const { routes } = useRoutes();

  return useMemo(() => {
    const rootUrl = routes.root.url;
    const sections: RouteEntry[] = [];
    const leaves: RouteEntry[] = [];

    Object.values(routes).forEach(route => {
      if (!route || typeof route !== "object") return;

      if (route.url && route.url !== rootUrl) {
        sections.push(route);
      }
      NESTED_KEYS.forEach(key => {
        if (route[key]?.url) leaves.push(route[key]);
      });
    });

    const leaf = longestMatch(leaves, pathname);
    const section = longestMatch(sections, pathname);
    const isRoot = pathname === rootUrl;
    const title = isRoot ? routes.root.name : (leaf || section)?.name || NOT_FOUND_TITLE;

    const crumbs: Crumb[] = [];

    if (!isRoot) {
      crumbs.push({ label: "Home", to: rootUrl });

      // Link the section only when the leaf is a page beneath it — routes.assets,
      // for one, is a namespace for /assets/detail and has no page of its own. A section with
      // no `labelKey` has no name to show either, and used to contribute a blank link.
      if (leaf && section && section.url !== leaf.url && section.name) {
        crumbs.push({ label: section.name, to: section.url });
      }
      crumbs.push({ label: title });
    }

    // The create action belongs to the list page, not to /users/create itself.
    const listPage = leaf ? undefined : section;

    return {
      crumbs,
      title,
      createAction: listPage?.create,
    };
  }, [pathname, routes]);
};

export default usePageBreadcrumbs;
